import { useEffect, useState } from "react";
import { http, formatMoney } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";

function statusBadge(status) {
  const map = {
    paid:     "bg-emerald-600",
    refunded: "bg-amber-500",
    failed:   "bg-red-500",
    pending:  "bg-zinc-400",
    expired:  "bg-zinc-400",
  };
  return <Badge className={`${map[status] || "bg-zinc-400"} text-white capitalize`}>{status}</Badge>;
}

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundOrder, setRefundOrder] = useState(null);
  const { currency, symbol } = useVenueMarket();
  const money = (p) => formatMoney(p, currency);

  const load = async () => {
    try {
      const { data } = await http.get("/orders");
      setOrders(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 lg:p-10 max-w-7xl" data-testid="orders-page">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Orders</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">All orders</h1>

      <div className="mt-8 rounded-xl bg-white border border-zinc-200 overflow-hidden">
        {loading && <div className="p-8 text-sm text-zinc-500">Loading…</div>}
        {!loading && orders.length === 0 && (
          <div className="p-12 text-center text-zinc-500">No orders yet.</div>
        )}
        {!loading && orders.length > 0 && (
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Order</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Buyer</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Tickets</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Total</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Status</th>
                <th className="text-right p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="p-4 font-mono text-xs">{o.order_number}</td>
                  <td className="p-4 text-sm">
                    <div className="font-medium">{o.buyer_name}</div>
                    <div className="text-xs text-zinc-500">{o.buyer_email}</div>
                  </td>
                  <td className="p-4 text-sm">
                    {(o.items || []).map((i, idx) => (
                      <div key={idx}>{i.quantity} × {i.tier_name}</div>
                    ))}
                  </td>
                  <td className="p-4 text-sm font-medium">
                    {money(o.total_pence)}
                    {o.refund_amount_pence > 0 && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        −{money(o.refund_amount_pence)} refunded
                      </div>
                    )}
                  </td>
                  <td className="p-4">{statusBadge(o.payment_status)}</td>
                  <td className="p-4 text-right">
                    {o.payment_status === "paid" && o.stripe_payment_intent && !o.refund_amount_pence && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`refund-btn-${o.id}`}
                        onClick={() => setRefundOrder(o)}
                        className="rounded-full"
                      >
                        <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Refund
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RefundDialog
        order={refundOrder}
        money={money}
        symbol={symbol}
        currency={currency}
        onClose={() => setRefundOrder(null)}
        onDone={() => {
          setRefundOrder(null);
          load();
        }}
      />
    </div>
  );
}

function RefundDialog({ order, money, symbol, currency, onClose, onDone }) {
  const [mode, setMode] = useState("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Reset form each time a new order is opened
    if (order) {
      setMode("full");
      setPartialAmount("");
      setReason("");
      setSubmitting(false);
    }
  }, [order]);

  if (!order) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {};
      if (mode === "partial") {
        const pounds = parseFloat(partialAmount);
        if (!pounds || pounds <= 0) {
          toast.error("Enter a refund amount");
          setSubmitting(false);
          return;
        }
        body.amount_pence = Math.round(pounds * 100);
        if (body.amount_pence > order.total_pence) {
          toast.error("Refund can't exceed the order total");
          setSubmitting(false);
          return;
        }
      }
      if (reason.trim()) body.reason = reason.trim();

      const { data } = await http.post(`/orders/${order.id}/refund`, body);
      toast.success(
        data.is_full_refund
          ? `Full refund of ${money(data.amount_pence)} sent to buyer`
          : `Partial refund of ${money(data.amount_pence)} sent to buyer`
      );
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Refund failed";
      toast.error(typeof msg === "string" ? msg : "Refund failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
      <DialogContent data-testid="refund-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund order {order.order_number}</DialogTitle>
          <DialogDescription>
            Buyer paid <strong>{money(order.total_pence)}</strong>. The refund goes straight back to their original card via Stripe — usually visible in 5–10 business days. This can&apos;t be undone.
          </DialogDescription>
        </DialogHeader>

        {/* Refund type toggle */}
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="refund-mode-full"
            onClick={() => setMode("full")}
            className={`flex-1 rounded-lg border p-3 text-left text-sm transition ${
              mode === "full" ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-300"
            }`}
          >
            <div className="font-bold">Full refund</div>
            <div className={`text-xs mt-0.5 ${mode === "full" ? "text-zinc-300" : "text-zinc-500"}`}>
              {money(order.total_pence)}
            </div>
          </button>
          <button
            type="button"
            data-testid="refund-mode-partial"
            onClick={() => setMode("partial")}
            className={`flex-1 rounded-lg border p-3 text-left text-sm transition ${
              mode === "partial" ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-300"
            }`}
          >
            <div className="font-bold">Partial</div>
            <div className={`text-xs mt-0.5 ${mode === "partial" ? "text-zinc-300" : "text-zinc-500"}`}>
              Custom amount
            </div>
          </button>
        </div>

        {mode === "partial" && (
          <div>
            <Label htmlFor="refund-amount">Amount to refund</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{symbol}</span>
              <Input
                id="refund-amount"
                data-testid="refund-partial-amount"
                type="number"
                step="0.01"
                min="0.01"
                max={(order.total_pence / 100).toFixed(2)}
                placeholder="0.00"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                className="pl-7"
              />
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Max {money(order.total_pence)} (the buyer&apos;s total)
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="refund-reason">Reason (optional, internal only)</Label>
          <Textarea
            id="refund-reason"
            data-testid="refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. event cancelled, duplicate purchase"
            rows={2}
            className="mt-1"
            maxLength={400}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="refund-cancel-btn"
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="refund-submit-btn"
            className="rounded-full bg-zinc-900 hover:bg-zinc-800"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refunding…</>
            ) : (
              <>Refund {mode === "full" ? money(order.total_pence) : (partialAmount ? money(Math.round(parseFloat(partialAmount || "0") * 100)) : "")}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
